/**
 * CameraView —— **全螢幕相機**（影相記帳用；所見即所拍）
 *
 * ⚠️ 為何要獨立成 component（2026-10-06 修 bug）：`<video>` 一定要**一開始就 mount**。
 *    原寫法係 `{live && <video ref={videoRef}/>}`，但 `open()` 喺 `setLive(true)` **之前**就讀
 *    `videoRef.current`（嗰刻仲係 null）→ stream 永遠掛唔上 video → 觀景器全黑（「相機唔 work」）。
 *    → 對策：video 無條件 render ＋ 用 `useEffect` 喺 mount 後掛 `srcObject`。
 *
 * 紅線：離開（unmount）→ 一定 `stopStream()` 停所有 track（唔准留低開住鏡頭）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  applyFocus, applyResolution, coverRegion, nextFacingMode, openCamera, stopStream,
  type FacingMode, type StreamLike,
} from '@coeldery/camera-capture/index.ts';
import {
  detectCorners, fullRasterFromVideo, rasterToDataUrl, rasterToJpeg, warpQuad, type Raster,
} from '@coeldery/document-scan/index.ts';
import CornerAdjust, { type Pt01 } from './CornerAdjust';

export default function CameraView({
  onCaptured, onClose,
}: {
  onCaptured: (blob: Blob, width: number, height: number) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const guideRef = useRef<HTMLSpanElement | null>(null);
  const streamRef = useRef<StreamLike | null>(null);
  const [facing, setFacing] = useState<FacingMode>('environment');
  const [ready, setReady] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [review, setReview] = useState<{ url: string; raster: Raster; corners: Pt01[] } | null>(null);

  // 開鏡頭／轉鏡頭；unmount 一定停所有 track
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setErr(null); setReady(false);
      try {
        const stream = await openCamera(facing, { getUserMedia: (c) => navigator.mediaDevices.getUserMedia(c) });
        if (cancelled) { stopStream(stream); return; }
        streamRef.current = stream;
        const v = videoRef.current;
        if (v) {
          v.srcObject = stream as unknown as MediaStream;
          await v.play().catch(() => undefined);
        }
        void applyFocus(stream, { mode: 'continuous' });
        void applyResolution(stream, 2560, 1440);   // 再試拉高解像度（唔支援就靜默）
        if (!cancelled) setReady(true);
      } catch (e) {
        if (!cancelled) setErr(t('capture.denied', { msg: e instanceof Error ? e.message : String(e) }));
      }
    })();
    return () => { cancelled = true; stopStream(streamRef.current); streamRef.current = null; };
  }, [facing, t]);

  // 切走（tab／app 轉背景）→ 即刻停鏡頭（紅線：唔准留低開住）
  useEffect(() => {
    const onHide = () => { if (document.visibilityState === 'hidden') onClose(); };
    document.addEventListener('visibilitychange', onHide);
    return () => document.removeEventListener('visibilitychange', onHide);
  }, [onClose]);

  // tap-to-focus（唔支援就靜默；座標用顯示框正規化 0–1）
  const focusAt = useCallback((e: React.PointerEvent<HTMLVideoElement>) => {
    const s = streamRef.current;
    if (!s) return;
    const r = e.currentTarget.getBoundingClientRect();
    void applyFocus(s, { points: [{ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height }] });
  }, []);

  /** 影相：抽全解像度一格 → 預填四角（偵測最佳努力；唔得就用取景框）→ 開「拉四角」畫面 */
  const shoot = useCallback(() => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const full = fullRasterFromVideo(v);
    if (!full) return;
    const box = v.getBoundingClientRect();
    const region = coverRegion(
      { width: full.width, height: full.height },
      { width: box.width || v.clientWidth || full.width, height: box.height || v.clientHeight || full.height },
    );
    const q = detectCorners(full);                 // 最佳努力：攞到就預填，唔到就用框
    let corners: Pt01[];
    if (q) {
      corners = q.map((p) => ({ x: p.x / full.width, y: p.y / full.height }));
    } else {
      const g = guideRef.current?.getBoundingClientRect();
      const toX = (x: number) => (region.sx + ((x - box.left) * region.sw) / (box.width || 1)) / full.width;
      const toY = (y: number) => (region.sy + ((y - box.top) * region.sh) / (box.height || 1)) / full.height;
      corners = g && g.width
        ? [
            { x: toX(g.left), y: toY(g.top) }, { x: toX(g.right), y: toY(g.top) },
            { x: toX(g.right), y: toY(g.bottom) }, { x: toX(g.left), y: toY(g.bottom) },
          ]
        : [{ x: 0.1, y: 0.1 }, { x: 0.9, y: 0.1 }, { x: 0.9, y: 0.9 }, { x: 0.1, y: 0.9 }];
    }
    setReview({ url: rasterToDataUrl(full), raster: full, corners });
  }, []);

  /** 確認拉平 → 透視矯正裁剪 → 交出去（上載）*/
  const apply = useCallback(async (corners01: Pt01[]) => {
    if (!review) return;
    const { raster } = review;
    const px = corners01.map((p) => ({ x: p.x * raster.width, y: p.y * raster.height }));
    const out = warpQuad(raster, px, { maxEdge: 1600 });
    const blob = await rasterToJpeg(out, 0.92);
    setReview(null);
    if (blob) onCaptured(blob, out.width, out.height);
  }, [review, onCaptured]);

  if (review) {
    return (
      <CornerAdjust
        url={review.url}
        initial={review.corners}
        onCancel={() => setReview(null)}
        onConfirm={(c) => void apply(c)}
      />
    );
  }

  return (
    <div className="camera-overlay">
      <video ref={videoRef} className="camera-video" playsInline muted autoPlay onPointerDown={focusAt} />
      <div className="camera-guide" aria-hidden="true"><span ref={guideRef} /></div>
      <p className="camera-hint">{!ready ? t('capture.opening') : t('capture.guide')}</p>
      {err && <p className="camera-err">{err}</p>}
      <div className="camera-controls">
        <button type="button" className="camera-side" onClick={onClose} aria-label={t('capture.cancel')}>✕</button>
        <button type="button" className="camera-shutter" onClick={() => shoot()} disabled={!ready} aria-label={t('capture.shoot')} />
        <button type="button" className="camera-side" onClick={() => setFacing(nextFacingMode(facing))} disabled={!ready} aria-label={t('capture.flip')}>🔄</button>
      </div>
    </div>
  );
}
